/* ==========================================================
   component-article-audio.js
   Custom element <article-audio>

   Zakłada obecność w środku:
     audio.article-audio__el
     [data-role="toggle"] [data-role="icon-play"] [data-role="icon-pause"]
     [data-role="seek"] [data-role="current"] [data-role="total"]
     [data-role="rate"]  (opcjonalny)
   ========================================================== */

if (!customElements.get('article-audio')) {
  class ArticleAudio extends HTMLElement {
    constructor() {
      super();
      this.rates = [1, 1.25, 1.5, 0.75];
      this.rateIndex = 0;
      this.milestones = {};
      this.dockDismissed = false;
      this.dock = null;
    }

    connectedCallback() {
      this.audio = this.querySelector('.article-audio__el');
      if (!this.audio) return;

      this.toggleBtn = this.querySelector('[data-role="toggle"]');
      this.iconPlay = this.querySelector('[data-role="icon-play"]');
      this.iconPause = this.querySelector('[data-role="icon-pause"]');
      this.seek = this.querySelector('[data-role="seek"]');
      this.currentEl = this.querySelector('[data-role="current"]');
      this.totalEl = this.querySelector('[data-role="total"]');
      this.rateBtn = this.querySelector('[data-role="rate"]');

      this.storageKey = 'selsey-audio-' + this.dataset.articleId;

      this.toggleBtn.addEventListener('click', () => this.togglePlay());

      this.seek.addEventListener('input', () => {
        if (this.audio.duration) {
          this.audio.currentTime = (this.seek.value / 100) * this.audio.duration;
        }
      });

      if (this.rateBtn) {
        this.rateBtn.addEventListener('click', () => this.cycleRate());
      }

      this.audio.addEventListener('loadedmetadata', () => this.onMetadata());
      this.audio.addEventListener('timeupdate', () => this.onTimeUpdate());
      this.audio.addEventListener('play', () => this.onPlay());
      this.audio.addEventListener('pause', () => this.setPlayingState(false));
      this.audio.addEventListener('ended', () => this.onEnded());

      if (this.dataset.dock === 'true') this.setupDock();
    }

    /* ---------- sterowanie ---------- */

    togglePlay() {
      if (this.audio.paused) {
        this.audio.play();
      } else {
        this.audio.pause();
      }
    }

    cycleRate() {
      this.rateIndex = (this.rateIndex + 1) % this.rates.length;
      const rate = this.rates[this.rateIndex];
      this.audio.playbackRate = rate;
      const label = this.rateBtn.querySelector('.btn__text') || this.rateBtn;
      label.textContent = rate + '\u00D7';
      this.emit('audio_rate_change', { audio_rate: rate });
    }

    setPlayingState(playing) {
      this.iconPlay.toggleAttribute('hidden', playing);
      this.iconPause.toggleAttribute('hidden', !playing);
      this.toggleBtn.setAttribute(
        'aria-label',
        playing ? 'Zatrzymaj wersję audio' : 'Odtwórz wersję audio artykułu'
      );
      if (this.dockToggle) {
        this.dockToggle.innerHTML = playing
          ? '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 5h4v14H6zm8 0h4v14h-4z" fill="currentColor"/></svg>'
          : '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
      }
    }

    /* ---------- zdarzenia audio ---------- */

    onMetadata() {
      this.totalEl.textContent = ArticleAudio.formatTime(this.audio.duration);
      const saved = parseFloat(sessionStorage.getItem(this.storageKey) || '0');
      if (saved > 5 && saved < this.audio.duration - 5) {
        this.audio.currentTime = saved;
      }
    }

    onTimeUpdate() {
      if (!this.audio.duration) return;

      const pct = (this.audio.currentTime / this.audio.duration) * 100;
      const now = ArticleAudio.formatTime(this.audio.currentTime);
      const total = ArticleAudio.formatTime(this.audio.duration);

      this.seek.value = pct;
      this.seek.setAttribute('aria-valuetext', now + ' z ' + total);
      this.currentEl.textContent = now;

      if (this.dockTime) this.dockTime.textContent = now + ' / ' + total;
      if (this.dockBar) this.dockBar.style.width = pct + '%';

      try {
        sessionStorage.setItem(this.storageKey, this.audio.currentTime);
      } catch (e) {
        /* tryb prywatny — pomijamy */
      }

      [25, 50, 75].forEach((m) => {
        if (pct >= m && !this.milestones[m]) {
          this.milestones[m] = true;
          this.emit('audio_progress', { audio_percent: m });
        }
      });
    }

    onPlay() {
      this.setPlayingState(true);
      this.emit('audio_play');
      this.setupMediaSession();
      if (this.dock && !this.dockDismissed && this.isScrolledPast()) {
        this.showDock(true);
      }
    }

    onEnded() {
      this.setPlayingState(false);
      this.emit('audio_complete');
      this.showDock(false);
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        /* noop */
      }
    }

    /* ---------- mini-player ---------- */

    setupDock() {
      const dock = document.createElement('div');
      dock.className = 'article-audio-dock';
      dock.innerHTML =
        '<div class="article-audio-dock__progress"><span></span></div>' +
        '<button type="button" class="article-audio-dock__toggle" aria-label="Odtwórz / zatrzymaj"></button>' +
        '<span class="article-audio-dock__title"></span>' +
        '<span class="article-audio-dock__time">0:00</span>' +
        '<button type="button" class="article-audio-dock__close" aria-label="Zamknij odtwarzacz">&times;</button>';

      // Do <body>, żeby position:fixed nie łapało transformów sekcji.
      document.body.appendChild(dock);

      this.dock = dock;
      this.dockToggle = dock.querySelector('.article-audio-dock__toggle');
      this.dockTime = dock.querySelector('.article-audio-dock__time');
      this.dockBar = dock.querySelector('.article-audio-dock__progress > span');
      dock.querySelector('.article-audio-dock__title').textContent = this.dataset.title || '';

      this.setPlayingState(false);

      this.dockToggle.addEventListener('click', () => this.togglePlay());
      dock.querySelector('.article-audio-dock__close').addEventListener('click', () => {
        this.dockDismissed = true;
        this.showDock(false);
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const out = !entries[0].isIntersecting;
          this.showDock(out && !this.audio.paused && !this.dockDismissed);
        });
        this.observer.observe(this);
      }
    }

    showDock(visible) {
      if (!this.dock) return;
      this.dock.classList.toggle('is-visible', !!visible);
    }

    isScrolledPast() {
      return this.getBoundingClientRect().bottom < 0;
    }

    /* ---------- ekran blokady / kontrolki systemowe ---------- */

    setupMediaSession() {
      if (!('mediaSession' in navigator) || this.mediaSessionReady) return;

      const artwork = this.dataset.artwork
        ? [{ src: this.dataset.artwork, sizes: '512x512', type: 'image/jpeg' }]
        : [];

      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.dataset.title || '',
        artist: this.dataset.shop || '',
        artwork: artwork,
      });

      navigator.mediaSession.setActionHandler('play', () => this.audio.play());
      navigator.mediaSession.setActionHandler('pause', () => this.audio.pause());
      navigator.mediaSession.setActionHandler('seekbackward', () => {
        this.audio.currentTime = Math.max(0, this.audio.currentTime - 15);
      });
      navigator.mediaSession.setActionHandler('seekforward', () => {
        this.audio.currentTime = Math.min(this.audio.duration, this.audio.currentTime + 15);
      });

      this.mediaSessionReady = true;
    }

    /* ---------- pomiar ---------- */

    emit(name, params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign(
          {
            event: name,
            article_id: this.dataset.articleId,
            article_title: this.dataset.title,
          },
          params || {}
        )
      );
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.dock && this.dock.parentNode) this.dock.parentNode.removeChild(this.dock);
    }

    static formatTime(seconds) {
      if (!isFinite(seconds)) return '0:00';
      const total = Math.floor(seconds);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return m + ':' + (s < 10 ? '0' : '') + s;
    }
  }

  customElements.define('article-audio', ArticleAudio);
}
